/**
 * Real, Shopify-calculated shipping options for the product page.
 * Pairs with snippets/shipping-by-country.liquid.
 *
 * Uses the Storefront API to create a temporary cart for the
 * current product variant, attach a delivery address for the
 * signed-in customer's own country (from their default address),
 * and read back the real shipping options/prices Shopify's own
 * shipping engine computes for that address — based on your actual
 * shipping zones, weight-based / price-based rates, free-shipping
 * thresholds, etc. Nothing here is hardcoded, and there's no country
 * picker — it always uses the customer's profile country.
 */
 
(function () {
  // ---- ONE-TIME SETUP -----------------------------------------
  // Paste your Storefront API access token here (see setup steps
  // in snippets/shipping-by-country.liquid). This token is safe to
  // expose client-side — it only allows storefront-level actions.
  var STOREFRONT_ACCESS_TOKEN = "8f1a4a371c0e0ef05ed62f73dee295cb";
  var API_VERSION = "2025-01"; // bump as needed
  // ----------------------------------------------------------------

  var container = document.getElementById("shipping-estimate");
  if (!container) return;
 
  var shopDomain = container.getAttribute("data-shop-domain");
  var variantId = container.getAttribute("data-variant-id");
  var countryCode = container.getAttribute("data-country-code");
  var address1 = container.getAttribute("data-address1");
  var city = container.getAttribute("data-city");
  var zip = container.getAttribute("data-zip");
  var provinceCode = container.getAttribute("data-province-code");
  var results = document.getElementById("shipping-estimate-results");
  var endpoint = "https://" + shopDomain + "/api/" + API_VERSION + "/graphql.json";
 
  // No results container means the customer isn't signed in / has no
  // saved address — the Liquid already shows a "sign in" message.
  if (!results || !countryCode) return;
 
  function graphql(query, variables) {
    return fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query: query, variables: variables }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (json) {
        // Always log the raw response so it's easy to inspect in the
        // browser console (Inspect > Console) when something goes wrong.
        console.log("[shipping-estimate] GraphQL response:", json);
 
        // Top-level GraphQL errors (bad token, missing scope, wrong
        // field/type, etc.) come back as { errors: [...] } with no
        // usable "data" — surface that clearly instead of letting
        // callers crash on `res.data.something`.
        if (json.errors && json.errors.length) {
          throw new Error(json.errors[0].message);
        }
        if (!json.data) {
          throw new Error("Empty response from Storefront API — check the token and API version.");
        }
        return json;
      });
  }
 
  var CART_CREATE = "mutation cartCreate($lines: [CartLineInput!]!, $country: CountryCode) {" +
    "  cartCreate(input: { lines: $lines, buyerIdentity: { countryCode: $country } }) {" +
    "    cart { id }" +
    "    userErrors { message }" +
    "  }" +
    "}";
 
  var ADD_DELIVERY_ADDRESS = "mutation cartDeliveryAddressesAdd($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {" +
    "  cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {" +
    "    cart {" +
    "      id" +
    "      deliveryGroups(first: 5) {" +
    "        nodes {" +
    "          deliveryOptions {" +
    "            handle" +
    "            title" +
    "            description" +
    "            estimatedCost { amount currencyCode }" +
    "          }" +
    "        }" +
    "      }" +
    "    }" +
    "    userErrors { message }" +
    "  }" +
    "}";
 
  // Carrier-calculated rates (live UPS/DHL/etc quotes) are NOT
  // included in a normal deliveryGroups query — Shopify only
  // computes them when explicitly asked via withCarrierRates: true,
  // and because that's a slow external API call, it has to be
  // requested through the @defer directive (a streamed response),
  // not a regular query. Requires Shopify Advanced or Plus.
  var CARRIER_RATES_QUERY = "query cartWithCarrierRates($cartId: ID!) {" +
    "  cart(id: $cartId) {" +
    "    id" +
    "    ...DeliveryGroups @defer(label: \"carrierRates\")" +
    "  }" +
    "}" +
    "fragment DeliveryGroups on Cart {" +
    "  deliveryGroups(first: 5, withCarrierRates: true) {" +
    "    nodes {" +
    "      deliveryOptions {" +
    "        handle" +
    "        title" +
    "        description" +
    "        estimatedCost { amount currencyCode }" +
    "      }" +
    "    }" +
    "  }" +
    "}";
 
  // Parses a multipart/mixed @defer response into an array of JSON
  // payloads (one per "--graphql"-delimited chunk).
  function parseMultipartMixed(text) {
    var payloads = [];
    text.split("--graphql").forEach(function (part) {
      part = part.trim();
      if (!part || part === "--") return;
      var splitAt = part.indexOf("\r\n\r\n");
      if (splitAt === -1) splitAt = part.indexOf("\n\n");
      var body = splitAt === -1 ? part : part.slice(splitAt).trim();
      try {
        payloads.push(JSON.parse(body));
      } catch (e) {
        // Not a JSON chunk (e.g. a stray boundary line) — skip it.
      }
    });
    return payloads;
  }
 
  // Fetches deliveryGroups WITH carrier-calculated rates via the
  // @defer streamed response. The spinner/"Calculating…" state is
  // kept on screen the whole time this is running (the caller does
  // not render the quick flat/zone rates until this settles), so the
  // visitor sees one loading state until everything — including the
  // slower external carrier lookups — has actually finished.
  // `fallbackGroups` (the quick rates from cartDeliveryAddressesAdd)
  // is what gets shown if this fails for any reason (store not on a
  // plan that supports carrier rates, a network hiccup, etc.).
  function fetchCarrierRates(cartId, fallbackGroups) {
    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "multipart/mixed",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query: CARRIER_RATES_QUERY, variables: { cartId: cartId } }),
    })
      .then(function (res) {
        return res.text();
      })
      .then(function (text) {
        var payloads = parseMultipartMixed(text);
        console.log("[shipping-estimate] carrier-rates payloads:", payloads);
 
        var groups = null;
        payloads.forEach(function (payload) {
          if (payload.data && payload.data.cart && payload.data.cart.deliveryGroups) {
            groups = payload.data.cart.deliveryGroups.nodes;
          }
          (payload.incremental || []).forEach(function (chunk) {
            if (chunk.data && chunk.data.deliveryGroups) {
              groups = chunk.data.deliveryGroups.nodes;
            }
          });
        });
 
        renderOptions(groups || fallbackGroups);
      })
      .catch(function (err) {
        console.log("[shipping-estimate] carrier-rates fetch failed (falling back to flat rates):", err);
        renderOptions(fallbackGroups);
      });
  }
 
  function formatMoney(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }).format(amount);
    } catch (e) {
      return amount + " " + currencyCode;
    }
  }
 
  function renderOptions(deliveryGroups) {
    var options = [];
    (deliveryGroups || []).forEach(function (group) {
      (group.deliveryOptions || []).forEach(function (opt) {
        options.push(opt);
      });
    });
 
    if (!options.length) {
      results.innerHTML = '<p class="shipping-estimate__error">No shipping options available for your country/address.</p>';
      return;
    }
 
    var html = "<ul>";
    options.forEach(function (opt) {
      var price = formatMoney(opt.estimatedCost.amount, opt.estimatedCost.currencyCode);
      html += "<li><span>" + (opt.title || opt.description || "Shipping") + "</span><strong>" + price + "</strong></li>";
    });
    html += "</ul>";
    results.innerHTML = html;
  }
 
  function fetchRates(countryCode) {
    if (!variantId) {
      results.innerHTML = '<p class="shipping-estimate__error">This product has no purchasable variant.</p>';
      return;
    }
    if (STOREFRONT_ACCESS_TOKEN.indexOf("PASTE_") === 0) {
      results.innerHTML = '<p class="shipping-estimate__error">Storefront API token not configured yet (see shipping-estimate.js).</p>';
      return;
    }
 
    results.innerHTML = '<p class="shipping-estimate__hint"><span class="shipping-estimate__spinner" aria-hidden="true"></span> Calculating shipping…</p>';
 
    var variantGid = "gid://shopify/ProductVariant/" + variantId;
 
    graphql(CART_CREATE, {
      lines: [{ merchandiseId: variantGid, quantity: 1 }],
      country: countryCode,
    })
      .then(function (res) {
        var errors = res.data && res.data.cartCreate && res.data.cartCreate.userErrors;
        if (errors && errors.length) throw new Error(errors[0].message);
        var cartId = res.data.cartCreate.cart.id;
 
        // Carrier-calculated rates (live UPS/DHL/etc quotes) need a
        // real address, not just a country — otherwise Shopify's
        // engine silently omits any rate provider that requires one
        // and you'll only see flat/zone-based rates.
        var deliveryAddress = { countryCode: countryCode };
        if (address1) deliveryAddress.address1 = address1;
        if (city) deliveryAddress.city = city;
        if (zip) deliveryAddress.zip = zip;
        if (provinceCode) deliveryAddress.provinceCode = provinceCode;
 
        return graphql(ADD_DELIVERY_ADDRESS, {
          cartId: cartId,
          addresses: [
            {
              address: { deliveryAddress: deliveryAddress },
              selected: true,
              oneTimeUse: true,
            },
          ],
        });
      })
      .then(function (res) {
        var errors = res.data && res.data.cartDeliveryAddressesAdd && res.data.cartDeliveryAddressesAdd.userErrors;
        if (errors && errors.length) throw new Error(errors[0].message);
        var cart = res.data.cartDeliveryAddressesAdd.cart;
 
        // Don't render these quick flat/zone rates yet — keep the
        // spinner up and wait for the (slower) carrier-calculated
        // rates too, so the visitor sees the complete list in one go
        // instead of a flash of partial results.
        fetchCarrierRates(cart.id, cart.deliveryGroups.nodes);
      })
      .catch(function (err) {
        results.innerHTML = '<p class="shipping-estimate__error">Could not load shipping options (' + err.message + ').</p>';
      });
  }
 
  fetchRates(countryCode);
})();
 